import api from "./api";
import {
  ok, getMe, can, ORG_SCOPES, roleKeysOf, primaryRoleOf, getLookupsCached, toEmployee, rememberName, nameOf,
  pMapSettled, GOAL_CATEGORIES, LOCATIONS, EMPLOYMENT_TO_API, ROLE_CODE_TO_KEY, ROLE_KEY_TO_CODE, utcToday,
  HR_ATTRIBUTE_FIELDS, hrAttributesFromRow,
} from "./core";
import { ensureItAdminRole } from "./rbacApi"; // [NEW] PM-5

export async function getMyProfile() {
  const me = await getMe(true);
  const [lookups, detail] = await Promise.all([getLookupsCached(), api.get(`/employees/${me.employee_id}`).then((r) => r.data)]);
  let reports = [];
  if (can(me, "team.performance.read")) {
    reports = await api.get("/employees/me/direct-reports").then((r) => r.data).catch(() => []);
  }
  reports.forEach((r) => rememberName(r.employee_id, r.employee_name));
  const roleKeys = roleKeysOf(me);
  const profile = toEmployee(detail, lookups, { role: primaryRoleOf(me), roles: roleKeys, directReportCount: reports.length });
  let manager = null;
  if (profile.managerId) {
    manager = await api
      .get("/employees/me/manager")
      .then((r) => {
        const m = r.data;
        if (!m?.employee_id) throw new Error("No reporting manager returned");
        rememberName(m.employee_id, m.employee_name);
        return {
          id: m.employee_id,
          name: m.employee_name,
          designationName: m.designation_name || "Reporting manager",
          email: m.official_email || "",
        };
      })
      .catch(() => {
        // Fallback if the endpoint fails or returns nothing
        const known = nameOf(profile.managerId);
        const unknown = known === profile.managerId;
        return {
          id: profile.managerId,
          name: unknown ? "your reporting manager" : known,
          designationName: unknown ? `Reporting manager, employee ID ${profile.managerId}` : "Reporting manager",
          email: "",
        };
      });
    profile.managerName = manager.name;
  }
  return ok({
    ...profile,
    username: me.username,
    grants: me.grants,
    roleKeys,
    mustChangePassword: me.must_change_password,
    directReports: reports.map((r) => r.employee_id),
    manager,
  });
}

export async function getLookups() {
  const [lookups, me] = await Promise.all([getLookupsCached(), getMe()]);
  let managers = [];
  if (can(me, "employee.manage", ORG_SCOPES)) {
    const data = await listAllEmployeeRows({ status: "ACTIVE" }).catch(() => []);
    managers = data.map((e) => {
      rememberName(e.employee_id, e.employee_name);
      return { id: e.employee_id, name: e.employee_name };
    });
  }
  return ok({ ...lookups, locations: LOCATIONS, goalCategories: GOAL_CATEGORIES, managers });
}

async function rolesFor(employeeId, me) {
  if (!can(me, "rbac.user_role.read")) return null;
  const { data } = await api.get(`/employees/${employeeId}/roles`);
  const now = Date.now();
  return data.filter((r) => !r.revoked_at && (!r.effective_to || new Date(`${r.effective_to}Z`).getTime() > now));
}

const rolePriority = ["admin", "hr", "management", "manager", "employee"];
const primaryFromAssignments = (rows) => {
  const keys = (rows || []).map((r) => ROLE_CODE_TO_KEY[r.role_code]).filter(Boolean);
  return rolePriority.find((k) => keys.includes(k)) || "employee";
};

async function listAllEmployeeRows(params = {}) {
  const rows = [];
  for (let offset = 0; offset < 20000; offset += 200) {
    const { data } = await api.get("/employees", { params: { ...params, limit: 200, offset } });
    rows.push(...data);
    if (data.length < 200) break;
  }
  return rows;
}

let employeeCache = { at: 0, key: "", promise: null };
const EMPLOYEE_CACHE_MS = 30 * 1000;
export function invalidateEmployees() { employeeCache = { at: 0, key: "", promise: null }; }

async function loadAllEmployees() {
  const [me, lookups] = await Promise.all([getMe(), getLookupsCached()]);
  const rolesVisible = can(me, "rbac.user_role.read");
  const key = `${me.employee_id}|${rolesVisible}`;
  if (employeeCache.promise && employeeCache.key === key && Date.now() - employeeCache.at < EMPLOYEE_CACHE_MS) return employeeCache.promise;
  const promise = (async () => {
    const data = await listAllEmployeeRows();
    data.forEach((e) => rememberName(e.employee_id, e.employee_name));
    const rows = await pMapSettled(data, async (e) => {
      const [detail, roles] = await Promise.all([
        api.get(`/employees/${e.employee_id}`).then((r) => r.data),
        rolesVisible ? rolesFor(e.employee_id, me).catch(() => null) : Promise.resolve(null),
      ]);
      // [FIXED] has_login / account_active come only on the list row, so keep them next to the detail record.
      return toEmployee({ has_login: e.has_login, account_active: e.account_active, ...detail }, lookups, {
        role: roles ? primaryFromAssignments(roles) : null,
        roles: roles ? [...new Set(["employee", ...roles.map((r) => ROLE_CODE_TO_KEY[r.role_code]).filter(Boolean)])] : [],
        roleAssignments: roles || [],
      });
    }, 8, null);
    const list = rows.filter(Boolean).map((e) => ({ ...e, rolesKnown: rolesVisible, managerName: e.managerId ? nameOf(e.managerId) : "—" }));
    const counts = {};
    list.forEach((x) => { if (x.managerId && x.status === "Active") counts[x.managerId] = (counts[x.managerId] || 0) + 1; });
    return list.map((e) => ({ ...e, directReportCount: counts[e.id] || 0, directReports: list.filter((x) => x.managerId === e.id && x.status === "Active").map((x) => ({ id: x.id, name: x.name })) }));
  })();
  employeeCache = { at: Date.now(), key, promise };
  promise.catch(() => invalidateEmployees());
  return promise;
}

// [UPDATED] PM-18: each filter may be one value or a list of values (checkbox dropdowns). A list matches when
// ANY of its values fits (OR); different filters must all fit (AND). "__none__" = the field is empty.
const wants = (v) => (Array.isArray(v) ? v : v ? [v] : []);
const fits = (want, have, same = (a, b) => a === b) => {
  const list = wants(want);
  return !list.length || list.some((w) => (w === "__none__" ? !have : same(have, String(w))));
};

export function filterEmployees(list = [], params = {}) {
  const q = String(params.search || "").trim().toLowerCase();
  return list.filter((e) => (!q || e.id.toLowerCase().includes(q) || e.name.toLowerCase().includes(q) || (e.email || "").toLowerCase().includes(q))
    && fits(params.departmentId, e.departmentId)
    && fits(params.status, e.status)
    && (!wants(params.role).length || wants(params.role).some((r) => e.role === r || (e.roles || []).includes(r)))
    && fits(params.managerId, e.managerId)
    && fits(params.costCenter, e.costCenter)
    && fits(params.businessUnit, e.businessUnit)
    && fits(params.location, e.location)
    && fits(params.legalEntity, e.legalEntity));
}

export async function getEmployees(params = {}, { fresh = false } = {}) {
  if (fresh) invalidateEmployees();
  const list = await loadAllEmployees();
  return ok(filterEmployees(list, params));
}

// [NEW] Employees with their organisation attributes (business unit, department, location, cost centre, legal entity),
// used by the shared filters (hooks/useOrgFilters.js) to look people up by id on any screen.
// HR / admin: every employee. Everyone else: their own direct reports (empty when they have none).
export async function getOrgDirectory() {
  const me = await getMe();
  if (can(me, "employee.manage", ORG_SCOPES)) {
    const list = await loadAllEmployees().catch(() => null);
    if (list) return ok(list);
  }
  const reports = await getMyDirectReports().then((r) => r.data).catch(() => []);
  return ok(reports);
}

export async function getEmployeeById(employeeId) {
  const lookups = await getLookupsCached();
  const { data } = await api.get(`/employees/${employeeId}`);
  return ok(toEmployee(data, lookups));
}

export async function getMyDirectReports() {
  const [me, lookups] = await Promise.all([getMe(), getLookupsCached()]);
  if (!can(me, "team.performance.read")) return ok([]);
  const { data } = await api.get("/employees/me/direct-reports");
  return ok(data.map((e) => toEmployee(e, lookups, { managerId: me.employee_id })));
}

function employeeBody(form, { forUpdate = false } = {}) {
  const status = EMPLOYMENT_TO_API[form.status] || "ACTIVE";
  return {
    employee_id: String(form.id || "").trim(),
    employee_name: String(form.name || "").trim(),
    official_email: String(form.email || "").trim().toLowerCase(),
    department_id: Number(form.departmentId),
    designation_id: Number(form.designationId),
    location: form.location || null,
    date_of_joining: form.dateOfJoining,
    employment_status: status,
    exit_date: status === "EXITED" ? form.exitDate || utcToday() : null,
    manager_employee_id: forUpdate ? null : form.managerId || null,
    reporting_effective_from: forUpdate || !form.managerId ? null : form.reportingFrom || form.dateOfJoining,
    // [NEW] migration 010 HR attributes. The backend rejects blank strings, so empty values are sent as null.
    ...Object.fromEntries(HR_ATTRIBUTE_FIELDS.map(([key, api]) => {
      const value = String(form[key] ?? "").trim();
      return [api, value ? (api === "personal_email" ? value.toLowerCase() : value) : null];
    })),
    reason: form.reason || (forUpdate ? "Employee details updated" : "Employee added"),
  };
}

export const MANAGEABLE_ROLE_KEYS = ["admin", "it_admin", "hr", "management", "manager", "employee"];

export async function setEmployeeRoles(employeeId, roleKeys = [], { reason } = {}) {
  const me = await getMe();
  if (employeeId === me.employee_id) throw new Error("You cannot change your own roles.");
  if (!can(me, "rbac.user_role.grant")) throw new Error("Only an administrator can change application roles.");
  const [{ data: roles }, { data: assignments }] = await Promise.all([
    api.get("/roles"),
    api.get(`/employees/${employeeId}/roles`),
  ]);
  const now = Date.now();
  const active = assignments.filter((a) => !a.revoked_at && (!a.effective_to || new Date(`${a.effective_to}Z`).getTime() > now));
  const wanted = new Set(["EMPLOYEE", ...roleKeys.map((k) => ROLE_KEY_TO_CODE[k])].filter(Boolean));
  const managed = new Set(MANAGEABLE_ROLE_KEYS.map((k) => ROLE_KEY_TO_CODE[k]));
  const why = reason || "Application roles updated";
  const added = [];
  const removed = [];
  for (const code of wanted) {
    if (!active.some((a) => a.role_code === code)) {
      let role = roles.find((r) => r.role_code === code && r.is_active);
      // [NEW] PM-5: the IT Admin role is created on first use, with the permissions IT needs (existing role APIs).
      if (!role && code === "IT_ADMIN") role = await ensureItAdminRole();
      if (!role) throw new Error(`Role ${code} is not set up in the backend.`);
      await api.post(`/employees/${employeeId}/roles`, { role_id: role.role_id, reason: why });
      added.push(code);
    }
  }
  for (const a of active) {
    if (managed.has(a.role_code) && !wanted.has(a.role_code)) {
      await api.post(`/user-roles/${a.user_role_id}/revoke`, { reason: why });
      removed.push(a.role_code);
    }
  }
  invalidateEmployees();
  return ok({ id: employeeId, added, removed });
}

export async function addEmployeeRole(employeeId, roleKey, reason = "Application role added") {
  const [{ data: roles }, { data: assignments }] = await Promise.all([api.get("/roles"), api.get(`/employees/${employeeId}/roles`)]);
  for (const code of new Set(["EMPLOYEE", ROLE_KEY_TO_CODE[roleKey]].filter(Boolean))) {
    if (assignments.some((a) => !a.revoked_at && a.role_code === code)) continue;
    const role = roles.find((r) => r.role_code === code && r.is_active);
    if (!role) throw new Error(`Role ${code} is not set up in the backend.`);
    await api.post(`/employees/${employeeId}/roles`, { role_id: role.role_id, reason });
  }
  invalidateEmployees();
  return ok({ id: employeeId });
}

export async function ensureEmployeeRole(employeeId) {
  const [{ data: roles }, { data: assignments }] = await Promise.all([api.get("/roles"), api.get(`/employees/${employeeId}/roles`)]);
  if (assignments.some((a) => !a.revoked_at && a.role_code === "EMPLOYEE")) return ok({ added: false });
  const role = roles.find((r) => r.role_code === "EMPLOYEE" && r.is_active);
  if (!role) throw new Error("The EMPLOYEE role is not set up in the backend.");
  await api.post(`/employees/${employeeId}/roles`, { role_id: role.role_id, reason: "Login created" });
  return ok({ added: true });
}

async function applyRoleIfAllowed(employeeId, roleKey) {
  const me = await getMe();
  if (!roleKey) return null;
  if (!can(me, "rbac.user_role.grant")) {
    return roleKey === "employee" ? null : "Saved. Ask an administrator to set the application role (Users & roles).";
  }
  await addEmployeeRole(employeeId, roleKey, `Application role set when the employee was added`);
  return null;
}

async function reportingStart(form) {
  if (!form.managerId) return null;
  const manager = await api.get(`/employees/${form.managerId}`).then((r) => r.data).catch(() => null);
  const joined = form.dateOfJoining;
  if (manager?.date_of_joining && manager.date_of_joining > joined) return manager.date_of_joining;
  return joined;
}

export async function createEmployee(form) {
  const reportingFrom = await reportingStart(form);
  const { data } = await api.post("/employees", employeeBody({ ...form, reportingFrom }));
  invalidateEmployees();
  const warning = await applyRoleIfAllowed(data.employee_id, form.role).catch((err) => err.message);
  return ok({ ...data, warning });
}

function describeChanges(before, form, lookups) {
  const out = [];
  const add = (label, a, b) => { if (String(a ?? "") !== String(b ?? "")) out.push(`${label}: ${a || "—"} → ${b || "—"}`); };
  add("name", before.employee_name, String(form.name || "").trim());
  add("email", before.official_email, String(form.email || "").trim().toLowerCase());
  const dep = (id) => lookups?.departments?.find((d) => d.id === String(id))?.name || id;
  const des = (id) => lookups?.designations?.find((d) => d.id === String(id))?.name || id;
  add("department", dep(before.department_id), dep(form.departmentId));
  add("designation", des(before.designation_id), des(form.designationId));
  add("location", before.location, form.location);
  // [NEW] HR attributes: only the field names go into the audit reason, not the personal values.
  const hrChanged = HR_ATTRIBUTE_FIELDS
    .filter(([key, api]) => String(before[api] ?? "") !== String(form[key] ?? "").trim())
    .map(([, api]) => api.replace(/_/g, " "));
  if (hrChanged.length) out.push(`HR details: ${hrChanged.join(", ")}`);
  add("status", before.employment_status, EMPLOYMENT_TO_API[form.status] || form.status);
  return out.length ? `Updated ${out.join("; ")}`.slice(0, 1000) : "Employee details saved (no field changed)";
}

export async function updateEmployee(employeeId, form) {
  const [{ data: before }, lookups] = await Promise.all([api.get(`/employees/${employeeId}`), getLookupsCached()]);
  const body = employeeBody({ ...form, id: employeeId, reason: form.reason || describeChanges(before, form, lookups) }, { forUpdate: true });
  if (form.replacementManagerId) body.replacement_manager_id = form.replacementManagerId;
  const currentManager = [...(before.reporting_history || [])].reverse().find((h) => !h.effective_to)?.manager_employee_id || null;
  const managerChanged = Boolean(form.managerId) && (form.managerId || null) !== currentManager;
  if (managerChanged) {
    await api.post(`/employees/${employeeId}/reporting`, {
      manager_employee_id: form.managerId,
      effective_from: utcToday(),
      reason: `Reporting manager changed: ${nameOf(currentManager, "none")} → ${nameOf(form.managerId)}`,
    });
  }
  let data;
  try {
    ({ data } = await api.put(`/employees/${employeeId}`, body));
  } catch (err) {
    invalidateEmployees();
    if (managerChanged) {
      const msg = err?.response?.data?.error?.message || err.message;
      throw new Error(`The new reporting manager was saved, but the other details were not: ${msg}. Fix it and save again.`);
    }
    throw err;
  }
  let warning = null;
  if (Array.isArray(form.roleKeys) && form.rolesTouched) {
    warning = await setEmployeeRoles(employeeId, form.roleKeys).then(() => null).catch((err) => `Details saved, but roles were not changed: ${err.message}`);
  }
  invalidateEmployees();
  return ok({ ...data, warning });
}

export async function updateEmployeeStatus(employeeId, status, { replacementManagerId, reason } = {}) {
  const me = await getMe();
  if (employeeId === me.employee_id) throw new Error("You cannot change your own status.");
  const { data: current } = await api.get(`/employees/${employeeId}`);
  const body = {
    employee_id: current.employee_id,
    employee_name: current.employee_name,
    official_email: current.official_email,
    department_id: current.department_id,
    designation_id: current.designation_id,
    location: current.location,
    date_of_joining: current.date_of_joining,
    employment_status: EMPLOYMENT_TO_API[status] || status,
    exit_date: (EMPLOYMENT_TO_API[status] || status) === "EXITED" ? utcToday() : null,
    manager_employee_id: null,
    reporting_effective_from: null,
    // [FIX] PUT replaces every column; keep the migration 010 HR attributes unchanged.
    ...hrAttributesFromRow(current),
    reason: reason || `Status changed to ${status}`,
  };
  if (replacementManagerId) body.replacement_manager_id = replacementManagerId;
  const { data } = await api.put(`/employees/${employeeId}`, body);
  invalidateEmployees();
  return ok(data);
}

export async function bulkImportEmployees(rows = []) {
  const lookups = await getLookupsCached();
  const errors = [];
  const byName = (list, value) => list.find((x) => x.name.toLowerCase() === String(value || "").trim().toLowerCase() || x.code?.toLowerCase() === String(value || "").trim().toLowerCase());
  const employees = rows.map((row, index) => {
    const department = byName(lookups.departments, row.department);
    const designation = byName(lookups.designations, row.designation);
    if (!department) errors.push({ row: index + 2, employeeId: row.employee_id || "—", message: `Unknown department "${row.department}".` });
    if (!designation) errors.push({ row: index + 2, employeeId: row.employee_id || "—", message: `Unknown designation "${row.designation}".` });
    const status = EMPLOYMENT_TO_API[(row.status || "Active").trim()] || "ACTIVE";
    return {
      employee_id: String(row.employee_id || "").trim(),
      employee_name: String(row.name || "").trim(),
      official_email: String(row.email || "").trim().toLowerCase(),
      department_id: Number(department?.id || 0),
      designation_id: Number(designation?.id || 0),
      location: row.location || null,
      date_of_joining: row.date_of_joining,
      employment_status: status,
      exit_date: status === "EXITED" ? utcToday() : null,
      manager_employee_id: row.manager_id || null,
      reporting_effective_from: row.manager_id ? row.date_of_joining : null,
      reason: "Bulk import",
    };
  });
  const roleKeyOf = (row) => {
    const text = String(row.role || "employee").trim().toLowerCase();
    return Object.keys(ROLE_KEY_TO_CODE).find((k) => k === text || ROLE_KEY_TO_CODE[k].toLowerCase() === text) || null;
  };
  rows.forEach((row, index) => {
    if (row.role && !roleKeyOf(row)) errors.push({ row: index + 2, employeeId: row.employee_id || "—", message: `Unknown role "${row.role}" (use employee, manager, hr, management or admin).` });
  });
  if (errors.length) return ok({ created: 0, errors });
  let createdIds = [];
  try {
    const { data } = await api.post("/employee-imports", { employees, reason: "Bulk employee import" });
    createdIds = data.employee_ids || employees.map((e) => e.employee_id);
  } catch (err) {
    const message = err?.response?.data?.error?.message || "Import failed. No rows were created.";
    return ok({ created: 0, errors: [{ row: "—", employeeId: "—", message: `${message} (the import is all-or-nothing)` }] });
  }
  invalidateEmployees();
  const me = await getMe();
  if (!can(me, "rbac.user_role.grant")) {
    return ok({ created: createdIds.length, errors: [], roleWarning: "Ask an administrator to set their roles under Users & roles (you can't assign roles)." });
  }
  const roleErrors = [];
  await pMapSettled(employees.filter((e) => e.employment_status === "ACTIVE" && createdIds.includes(e.employee_id)), async (e) => {
    const row = rows.find((r) => String(r.employee_id || "").trim() === e.employee_id);
    try {
      await addEmployeeRole(e.employee_id, roleKeyOf(row) || "employee", "Role from bulk import");
    } catch (err) {
      roleErrors.push({ row: "—", employeeId: e.employee_id, message: `Employee created, but the role could not be set: ${err?.response?.data?.error?.message || err.message}` });
    }
  }, 4);
  return ok({ created: createdIds.length, errors: roleErrors });
}